import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ArrowRight, Linkedin, Twitter, Github, Phone, Mail, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const Footer = ({ onBookNow }) => {
  const { isDarkMode } = useTheme();
  const currentYear = new Date().getFullYear();
  const [isCeoModalOpen, setIsCeoModalOpen] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);

  const ceoInfo = {
    name: "Peter Gwaze",
    title: "Founder & CEO",
    photo: "/peter-gwaze.jpg",
    shortBio: "Peter Gwaze is the Founder and CEO of NEURA AI SOFTWARES and a fully certified Telecommunications Technician. He leads a cutting-edge artificial intelligence consultancy dedicated to helping businesses harness the power of AI to drive innovation, efficiency, and growth.",
    fullBio: "Peter Gwaze is the Founder and CEO of NEURA AI SOFTWARES and a fully certified Telecommunications Technician. He leads a cutting-edge artificial intelligence consultancy dedicated to helping businesses harness the power of AI to drive innovation, efficiency, and growth. With a deep passion for technology and a visionary approach to business transformation, Peter founded NEURA AI SOFTWARES to bridge the gap between complex AI solutions and practical, real-world applications for enterprises of all sizes."
  };

  const footerLinks = {
    company: [
      { label: 'About', href: '#about' },
      { label: 'Growth Engine', href: '#packages' },
      { label: 'Contact', href: '#contact' }
    ],
    services: [
      { label: 'AI Website Interface', href: '#packages' },
      { label: 'Lead Qualification', href: '#packages' },
      { label: 'Automated Follow-up', href: '#packages' },
      { label: 'CRM Integration', href: '#packages' }
    ],
    resources: [
      { label: 'Case Studies', href: '/case-studies' },
      { label: 'Client Reviews', href: '/reviews' },
      { label: 'Blog', href: '#' },
      { label: 'FAQs', href: '#' },
      { label: 'Support', href: '#contact' }
    ]
  };

  return (
    <footer className={`relative border-t transition-colors duration-300 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} data-testid="footer">
      {/* CTA Section */}
      <div className={`relative py-12 sm:py-24 ${isDarkMode ? 'bg-gradient-to-b from-gray-800 to-gray-900' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] sm:w-[800px] h-[200px] sm:h-[400px] rounded-full blur-[100px] sm:blur-[150px] ${isDarkMode ? 'bg-cyan-900/30' : 'bg-cyan-100/50'}`} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className={`text-2xl sm:text-4xl md:text-5xl font-extralight mb-4 sm:mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Ready to Grow Your
            <span className={`font-normal ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}> Business Online?</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
            Get a FREE quote and see how we can help you attract more customers with a stunning website.
          </p>

          <div>
            <Button
              onClick={onBookNow}
              size="lg"
              className="dynamic-button dynamic-button-gradient bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-medium px-6 sm:px-10 py-4 sm:py-6 text-base sm:text-lg hover:from-cyan-600 hover:to-purple-600 transition-all duration-300 shadow-lg shadow-purple-500/25"
              data-testid="footer-book-call"
            >
              FREE QUOTE
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-12">
            {/* Brand */}
            <div className="col-span-2 sm:col-span-2 lg:col-span-2">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                  <span className="text-white font-bold text-lg sm:text-xl">N</span>
                </div>
                <span className="text-base sm:text-xl font-light tracking-wide text-gray-900">
                  NEURA <span className="font-semibold text-cyan-600">AI</span>
                </span>
              </div>
              <p className="text-gray-500 mb-4 sm:mb-6 max-w-sm text-sm sm:text-base">
                Professional web design, SEO, e-commerce, and AI chatbots for UK businesses.
              </p>

              {/* CEO Profile Section */}
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-slate-50 to-cyan-50 border border-cyan-100">
                <Dialog open={isCeoModalOpen} onOpenChange={(open) => {
                  setIsCeoModalOpen(open);
                  if (!open) setIsBioExpanded(false);
                }}>
                  <DialogTrigger asChild>
                    <button 
                      className="flex items-center gap-3 text-left group/ceo w-full"
                      data-testid="ceo-info-trigger"
                    >
                      <img 
                        src={ceoInfo.photo} 
                        alt={ceoInfo.name}
                        className="w-16 h-20 rounded-lg object-cover shadow-md border-2 border-cyan-400/50 group-hover/ceo:border-cyan-500 transition-colors"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-gray-800">{ceoInfo.name}</span>
                        <span className="text-xs text-cyan-600 font-medium">{ceoInfo.title}</span>
                        <span className="text-[10px] text-orange-500 font-medium mt-1 flex items-center gap-1">
                          View Profile <ChevronDown className="w-3 h-3" />
                        </span>
                      </div>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md max-w-[90vw]" data-testid="ceo-modal">
                    <DialogHeader>
                      <DialogTitle className="sr-only">About {ceoInfo.name}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center text-center">
                      <div className="relative mb-4">
                        <img 
                          src={ceoInfo.photo} 
                          alt={ceoInfo.name}
                          className="w-32 h-44 sm:w-40 sm:h-52 rounded-xl object-cover shadow-xl border-4 border-cyan-500/50"
                        />
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">{ceoInfo.name}</h3>
                      <p className="text-sm text-cyan-600 font-medium mb-4">{ceoInfo.title}</p>
                      <div className="text-left w-full">
                        <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">
                          {isBioExpanded ? ceoInfo.fullBio : ceoInfo.shortBio}
                        </p>
                        <button
                          onClick={() => setIsBioExpanded(!isBioExpanded)}
                          className="mt-3 flex items-center gap-1 text-cyan-600 hover:text-cyan-700 font-medium text-sm transition-colors"
                          data-testid="read-more-btn"
                        >
                          {isBioExpanded ? (
                            <>Show Less <ChevronUp className="w-4 h-4" /></>
                          ) : (
                            <>Read More <ChevronDown className="w-4 h-4" /></>
                          )}
                        </button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Contact Details */}
              <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3 text-gray-600 text-sm sm:text-base">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600 flex-shrink-0" />
                  <span>Belfast, Northern Ireland</span>
                </div>

                <a href="tel:+447471343190" className="flex items-center gap-2 sm:gap-3 text-gray-600 hover:text-cyan-600 transition-colors text-sm sm:text-base">
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600 flex-shrink-0" />
                  <span>074 7134 3190</span>
                </a>
                <a href="mailto:PeterG@neura-ai.info" className="flex items-center gap-2 sm:gap-3 text-gray-600 hover:text-cyan-600 transition-colors text-sm sm:text-base break-all">
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="truncate">PeterG@neura-ai.info</span>
                </a>
              </div>

              <div className="flex gap-3 sm:gap-4">
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-cyan-600 hover:border-cyan-300 transition-all duration-300">
                  <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-cyan-600 hover:border-cyan-300 transition-all duration-300">
                  <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
                <a href="#" className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-400 hover:text-cyan-600 hover:border-cyan-300 transition-all duration-300">
                  <Github className="w-4 h-4 sm:w-5 sm:h-5" />
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-gray-900 font-medium mb-3 sm:mb-4 text-sm sm:text-base">Company</h4>
              <ul className="space-y-2 sm:space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-gray-500 hover:text-cyan-600 transition-colors duration-300 text-xs sm:text-sm">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-gray-900 font-medium mb-3 sm:mb-4 text-sm sm:text-base">Services</h4>
              <ul className="space-y-2 sm:space-y-3">
                {footerLinks.services.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-gray-500 hover:text-cyan-600 transition-colors duration-300 text-xs sm:text-sm">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <h4 className="text-gray-900 font-medium mb-3 sm:mb-4 text-sm sm:text-base">Resources</h4>
              <ul className="space-y-2 sm:space-y-3">
                {footerLinks.resources.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-gray-500 hover:text-cyan-600 transition-colors duration-300 text-xs sm:text-sm">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
            <div className="flex flex-col items-center justify-center gap-3 sm:gap-4 text-center">
              <p className="text-gray-400 text-xs sm:text-sm">
                &copy; {currentYear} NEURA AI SOFTWARES. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Link to="/privacy-policy" className="text-blue-500 hover:text-blue-600 text-xs sm:text-sm transition-colors font-medium">Privacy Policy</Link>
                <Link to="/terms-of-service" className="text-blue-500 hover:text-blue-600 text-xs sm:text-sm transition-colors font-medium">Terms of Service</Link>
                <Link to="/cookie-policy" className="text-blue-500 hover:text-blue-600 text-xs sm:text-sm transition-colors font-medium">Cookie Policy</Link>
                <Link to="/data-request" className="text-blue-500 hover:text-blue-600 text-xs sm:text-sm transition-colors font-medium">Data Request</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Google Maps Section */}
        <div id="contact" className="border-t border-gray-200 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-8">
            <h4 className="text-gray-900 font-medium mb-4 text-center text-sm sm:text-base">Our Location</h4>
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d73606.79457285144!2d-6.0034!3d54.5973!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4860fffdd7d08a3b%3A0x2e57162cefc7c531!2sBelfast%2C%20UK!5e0!3m2!1sen!2suk!4v1703123456789!5m2!1sen!2suk"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="NEURA AI SOFTWARES Location - Belfast, Northern Ireland"
                className="sm:h-[300px]"
              />
            </div>
            <p className="text-center text-gray-500 text-xs sm:text-sm mt-4">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 inline-block mr-1" />
              Belfast, Northern Ireland, United Kingdom
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
